import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, catchError, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../store/app.state';
import * as ErrorActions from 'src/app/shared/error/store/actions/error.actions';
import { OidcAuthService } from 'src/app/auth/services/oidc-auth.service';
import { AppConfigService } from 'src/app/app-config.service';
import { ErrorNotificationService } from 'src/app/shared/error/services/error-notification.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private store: Store<AppState>,
    private authService: OidcAuthService, // Inject OidcAuthService
    private configService: AppConfigService,
    private errorNotificationService: ErrorNotificationService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isProd = environment.production;

    if (!isProd) {
      console.log('[http_interceptor] >> Requête :', req.method, req.url);
    }

    // Laisser passer les requêtes hors-API (assets, discovery OIDC, etc.) sans contrôle d'auth
    const apiName = this.configService.resolveApiName(req.url);
    if (!apiName) {
      return next.handle(req);
    }

    // Pour les requêtes API : vérifier l'authentification avant tout
    if (!this.authService.isAuthenticated) {
      console.warn('[sbvp_gui][http_interceptor] User is not authenticated, redirecting to login.');
      this.redirectToLogin();
      return EMPTY; // Annuler la requête API — inutile de l'envoyer sans token
    }
    const apiConfig = this.configService.getApiConfig(apiName);

    let modifiedReq = req;
    const headersToAdd: { [key: string]: string } = {};
    // Préparer tous les headers à ajouter
    // const headersToAdd: { [key: string]: string } = {
    //   'x-auth-pivot': apiConfig.authType,
    // };

    if (apiConfig.authType === 'bearer') {
      const token = this.authService.getUserToken;
      if (!isProd) {
        console.log('[http_interceptor] JWT token obtained');
      }
      if (token) {
        headersToAdd['Authorization'] = `Bearer ${token}`;
      }
    } else if (apiConfig.authType === 'apikey') {
      const api_key = apiConfig.apiKey;
      if (api_key) {
        headersToAdd[apiConfig.apiKeyName || 'x-api-key'] = api_key;
      }
    }

    // Cloner la requête une seule fois avec tous les headers
    modifiedReq = req.clone({
      setHeaders: headersToAdd,
    });

    return next.handle(modifiedReq).pipe(
      catchError(error => {
        console.error('[http_interceptor] Erreur HTTP:', error?.status, error?.url);
        if (error instanceof HttpErrorResponse) {
          this.reportHttpErrorToDynatrace(error);

          // Gestion spécifique des erreurs 401 (Unauthorized)
          if (error.status === HttpStatusCode.Unauthorized || error.status === 401) {
            // Marquer l'erreur comme gérée par l'intercepteur
            (error as any)._handledByInterceptor = true;

            // Réinitialiser le store d'erreurs pour éviter l'affichage de toasts
            this.store.dispatch(ErrorActions.resetError());

            // Rediriger vers la page de login
            this.redirectToLogin();

            // Ne pas dispatcher l'erreur globale pour éviter l'affichage de toasts
            // L'erreur est quand même throw pour le reporting mais le GlobalErrorHandler ne redirigera pas
            return throwError(() => error);
          }

          // Gestion spécifique des erreurs 403 (Forbidden)
          if (error.status === HttpStatusCode.Forbidden || error.status === 403) {
            // Marquer l'erreur comme gérée par l'intercepteur
            (error as any)._handledByInterceptor = true;

            // Afficher la modal avec le message approprié
            this.errorNotificationService.showError({
              title: 'Fonctionnalité non autorisée',
              message:
                "La fonctionnalité que vous demandez ne vous est pas autorisée. Veuillez vous rapprocher d'un administrateur pour obtenir les droits nécessaires.",
              confirmLabel: 'OK',
              type: 'warning',
              isConfirm: true,
            });

            // Ne pas dispatcher l'erreur globale pour éviter la double gestion
            // L'erreur est quand même throw pour le reporting mais le GlobalErrorHandler ne redirigera pas
            return throwError(() => error);
          }

          // Formater le message d'erreur de manière sûre
          const formatErrorMessage = (err: any, httpError?: HttpErrorResponse): string => {
            // --- Intercepter spécifiquement les erreurs réseau/CORS status 0 ---
            if (error.status === 0) {
              return 'Erreur réseau : Impossible de joindre le serveur. (Vérifiez votre connexion ou la politique CORS du serveur distant).';
            }

            // Si c'est une chaîne, la retourner telle quelle (sauf si c'est un message générique Angular)
            if (typeof err === 'string') {
              // Filtrer les messages génériques d'Angular HttpClient
              if (err.includes('Http failure response for')) {
                if (httpError) {
                  return `Erreur ${httpError.status}: ${httpError.statusText || 'Erreur de communication'}`;
                }
                return 'Erreur de communication avec le serveur';
              }
              return err;
            }

            // Chercher un message dans les propriétés de l'objet
            if (err?.details && typeof err.details === 'string') {
              return err.details;
            }
            if (err?.message && typeof err.message === 'string') {
              if (err.message.includes('Http failure response for')) {
                if (httpError) {
                  return `Erreur ${httpError.status}: ${httpError.statusText || 'Erreur de communication'}`;
                }
                return 'Erreur de communication avec le serveur';
              }
              return err.message;
            }

            // Si l'objet a une propriété error, récursion
            if (err?.error) {
              return formatErrorMessage(err.error, httpError);
            }

            // Si c'est un objet, essayer de le convertir en JSON
            if (typeof err === 'object' && err !== null) {
              try {
                const jsonStr = JSON.stringify(err);
                if (jsonStr.length > 200) {
                  return 'Erreur technique — Détails disponibles dans la console';
                }
                return jsonStr;
              } catch (_e) {
                return 'Erreur inconnue';
              }
            }

            return 'Erreur inconnue';
          };

          const formattedMessage = formatErrorMessage(error.error || error, error);

          const err = {
            code: error.status === 0 ? 'RESEAU' : error.status || 999,
            status: error.status,
            message: formattedMessage,
            details: error.error?.detail || [],
            type: 'error',
            title: error.status === 0 ? 'Erreur de connexion' : error.statusText || 'Erreur',
          };

          this.store.dispatch(ErrorActions.globalError({ error: err }));
          return throwError(() => error);
        } else {
          console.error('[http_interceptor] Erreur non-HTTP:', error);
          this.store.dispatch(ErrorActions.globalError({ error: error }));
          return throwError(() => error);
        }
      }),
    );
  }

  private handleError(err: HttpErrorResponse): Observable<any> {
    if (err.status === HttpStatusCode.Unauthorized) {
      this.router.navigate(['/not-authorized']);
      return of();
    }
    return throwError(() => err);
  }

  private redirectToLogin() {
    this.router.navigate(['login'], {});
  }

  /**
   * Reporte DYNAMIQUEMENT toutes les erreurs HTTP à Dynatrace
   */
  private reportHttpErrorToDynatrace(error: HttpErrorResponse): void {
    if (typeof (window as any).dtrum === 'function') {
      try {
        const dynamicError = new Error(`HTTP ${error.status} - ${error.statusText || 'Unknown'}`);
        dynamicError.name = `HttpError${error.status}`;
        dynamicError.message = `HTTP ${error.status}: ${error.statusText || 'Unknown'} at ${error.url}`;

        dynamicError.stack = [
          `${dynamicError.name}: ${dynamicError.message}`,
          `    URL: ${error.url || 'unknown'}`,
          `    Status: ${error.status} (${error.statusText || 'Unknown'})`,
          `    at HttpInterceptor.catchError`,
        ].join('\n');

        (window as any).dtrum('reportError', dynamicError);
        (window as any).dtrum(
          'markXHRFailed',
          error.status,
          `${error.url} - ${error.statusText || error.message}`,
        );
      } catch (dynatraceError) {
        console.error('[HttpInterceptor] Erreur reporting Dynatrace:', dynatraceError);
      }
    }
  }
}
